/* eslint-disable prefer-arrow-callback */
import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { VIDEO_FILE_MIME_TYPES } from "@miplatform/constants";
import { useDocumentsViewModel } from "@miplatform/view-models";
import { mdiClose, mdiFileAlert, mdiFileCheck } from "@mdi/js";
import { Box, Typography, useTheme } from "@mui/material";
import { DocumentViewer } from "../DocumentViewer/DocumentViewer";
import { MiIcon } from "../MiIcon/MiIcon";
import { MiIconButton } from "../MiIconButton/MiIconButton";

/**
 * MiFileList component renders a list of files with options to preview and delete each file.
 *
 * @param {Object[]} files - Array of file objects to be displayed.
 * @param {Function} onDelete - Callback function to handle file deletion.
 * @param {Function} onFilePreview - Callback function to handle file preview.
 * @param {Function} renderFileCardActions - Function to render additional actions for each file card.
 *
 * @returns {JSX.Element} The rendered component.
 */
export const MiFileList = ({
  files = [],
  CustomFileCard,
  onDelete = () => null,
  onFilePreview,
  onSelect,
  selectedFile,
  renderFileCardActions = () => null,
  viewMode = false,
  testId = "",
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { printFile, downloadFile } = useDocumentsViewModel();
  const [viewingDocuments, setViewingDocuments] = useState([]);

  const handlePreviewOpen = (file, index) => {
    if (file.error) {
      return;
    }

    if (onFilePreview) {
      onFilePreview(file);
      return;
    }

    const otherFiles = files.filter((_, i) => i !== index);
    setViewingDocuments(
      [file, ...otherFiles].map((file) => ({
        ...file,
        contentType: file.type,
        tempURL: URL.createObjectURL(file),
      }))
    );
  };

  const handlePreviewClose = () => {
    viewingDocuments.forEach((doc) => {
      if (doc.tempURL) {
        URL.revokeObjectURL(doc.tempURL);
      }
    });
    setViewingDocuments([]);
  };

  const handleDelete = (e, file) => {
    e.stopPropagation();
    onDelete(file);
  };

  return (
    <Box className={classes.fileList} data-testid={`${testId}-file-list`}>
      {CustomFileCard
        ? files.map((file, index) => (
            <Box key={index}>
              <CustomFileCard
                document={file}
                disablePrint={VIDEO_FILE_MIME_TYPES.includes(file.contentType)}
                onPrint={() => printFile(file)}
                onDelete={(e) => handleDelete(e, file)}
                onDownload={() => downloadFile(file)}
                onSelect={() => onSelect(file)}
                selectedDoc={selectedFile?.id}
              />
            </Box>
          ))
        : files.map((file, index) => (
            <Box
              className={classes.fileCard}
              key={index}
              onClick={() => handlePreviewOpen(file, index)}
              data-testid={`${testId}-file-card-${index}`}
            >
              <Box className={classes.cardLeftContent}>
                <MiIcon
                  className={classes.cardIcon}
                  path={file.error ? mdiFileAlert : mdiFileCheck}
                  color={
                    file.error
                      ? theme.palette.red.main
                      : theme.palette.green.main
                  }
                />
                <Box className={classes.fileText}>
                  <Typography className={classes.fileName}>
                    {file?.name || file?.documentId}
                  </Typography>
                  {file.error && (
                    <Typography color={theme.palette.error.main} fontSize={12}>
                      {file.error}
                    </Typography>
                  )}
                  {renderFileCardActions(file)}
                </Box>
              </Box>
              {!viewMode && (
                <Box>
                  <MiIconButton onClick={(e) => handleDelete(e, file)}>
                    <MiIcon path={mdiClose} size={1} />
                  </MiIconButton>
                </Box>
              )}
            </Box>
          ))}
      <DocumentViewer
        documents={viewingDocuments}
        onUnfullscreen={handlePreviewClose}
        fullScreenStatus={viewingDocuments.length > 0}
      />
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  fileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    maxWidth: "100%",
  },
  fileCard: {
    flex: "1 1 auto",
    justifyContent: "space-between",
    alignItems: "center",
    border: `1px solid ${theme.palette.grey.accent}`,
    backgroundColor: theme.palette.grey.lightAccent,
    borderRadius: theme.spacing(1),
    padding: `0 ${theme.spacing(1)}`,
    display: "flex",
    cursor: "pointer",
  },
  cardLeftContent: {
    display: "flex",
    flexShrink: 1,
    overflow: "hidden",
    gap: theme.spacing(3),
    alignItems: "center",
  },
  cardIcon: {
    flexShrink: 0,
  },
  fileText: {
    textAlign: "left",
    flexShrink: 1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  fileName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
}));
